import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Pagination, Spinner, Alert } from 'react-bootstrap';
import { useRecoilState } from 'recoil';
import { notificationsState } from '../../atoms';
import notificationService from '../../services/notification.service';
import NotificationService from '../../services/notification.service';
import inService from '../../services/transfer-in-request.service';
import outService from '../../services/transfer-out-request.service';
import MessageAnotherPharmacyService from '../../services/MessageAnotherPharmacy.service';
import ViewTransferRequestModal from '../../components/ViewTransferRequestModal';
import ViewMessageAnotherPharmacyModal from '../../components/ViewMessageAnotherPharmacyModal';
import './index.scss';

const Notifications = () => {
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal states
  const [viewTransfer, setViewTransfer] = useState(false);
  const [viewMessageAnotherPharmacyModalShow, setViewMessageAnotherPharmacyModalShow] = useState(false);
  const [record, setRecord] = useState({});
  const [requestType, setRequestType] = useState({});
  const [currentNotification, setCurrentNotification] = useState({});

  const itemsPerPage = 10;

  useEffect(() => {
    fetchNotifications(currentPage);
  }, [currentPage]);

  const fetchNotifications = async (page) => {
    setLoading(true);
    setError(null);
    try {
      const result = await notificationService.GetAll(page);
      if (result.error === null) {
        setNotifications(result.data);
        // Calculate total pages based on response or assume more pages exist
        const totalItems = result.data?.totalCount || result.data?.notifications?.length || 0;
        setTotalPages(Math.max(1, Math.ceil(totalItems / itemsPerPage)));
      } else {
        setError('Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('An error occurred while fetching notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notification) => {
    try {
      const result = await NotificationService.MakeNotificationIsRead(notification);
      if (result.error === null) {
        fetchNotifications(currentPage);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markSelectedAsRead = async () => {
    try {
      // Use the new bulk update method if available, otherwise fall back to individual updates
      if (NotificationService.MakeMultipleNotificationsRead) {
        await NotificationService.MakeMultipleNotificationsRead(selectedNotifications);
      } else {
        const promises = selectedNotifications.map(notificationId => {
          const notification = notifications.notifications?.find(n => n.id === notificationId);
          return NotificationService.MakeNotificationIsRead(notification);
        });
        await Promise.all(promises);
      }
      
      setSelectedNotifications([]);
      setSelectAll(false);
      fetchNotifications(currentPage);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      setError('Failed to mark notifications as read');
    }
  };


  const getDetailedInById = async (notification) => {
    const result = await inService.GetById(notification?.RecordId);
    if (result?.error === null) {
      setRequestType(0);
      setRecord(result?.data);
      setViewTransfer(true);
      setCurrentNotification(notification);
    }
  };

  const getDetailedOutById = async (notification) => {
    const result = await outService.GetById(notification?.RecordId);
    if (result?.error === null) {
      setRequestType(1);
      setRecord(result?.data);
      setViewTransfer(true);
      setCurrentNotification(notification);
    }
  };

  const getDetailedMessageFromPharmacyById = async (notification) => {
    const result = await MessageAnotherPharmacyService.GetById(notification?.RecordId);
    if (result?.error === null) {
      setRecord(result?.data);
      setViewMessageAnotherPharmacyModalShow(true);
      setCurrentNotification(notification);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read when clicked
    if (notification.IsRead === 0) {
      await markAsRead(notification);
    }

    // Handle different notification types
    if (notification.Content.includes("Message from  pharmacy")) {
      await getDetailedMessageFromPharmacyById(notification);
    } else if (notification.Content.includes("transfer in")) {
      await getDetailedInById(notification);
    } else {
      await getDetailedOutById(notification);
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => {
      if (prev.includes(notificationId)) {
        return prev.filter(id => id !== notificationId);
      } else {
        return [...prev, notificationId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications([]);
    } else {
      const allIds = notifications.notifications?.map(n => n.id) || [];
      setSelectedNotifications(allIds);
    }
    setSelectAll(!selectAll);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (content) => {
    if (content.includes("Message from  pharmacy")) {
      return "fas fa-envelope";
    } else if (content.includes("transfer in")) {
      return "fas fa-arrow-down";
    } else if (content.includes("transfer out")) {
      return "fas fa-arrow-up";
    }
    return "fas fa-bell";
  };

  const getNotificationColor = (content) => {
    if (content.includes("Message from  pharmacy")) {
      return "info";
    } else if (content.includes("transfer in")) {
      return "success";
    } else if (content.includes("transfer out")) {
      return "warning";
    }
    return "primary";
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    if (startPage > 1) {
      pages.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    // Visible pages
    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      pages.push(
        <Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center">
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        />
        {pages}
        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        />
      </Pagination>
    );
  };

  return (
    <Container fluid className="notifications-page">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <Row className="align-items-center">
                <Col>
                  <h4 className="mb-0">
                    <i className="fas fa-bell me-2 text-primary"></i>
                    All Notifications
                  </h4>
                </Col>
                <Col xs="auto">
                  {selectedNotifications.length > 0 && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={markSelectedAsRead}
                      className="me-2"
                    >
                      <i className="fas fa-check me-1"></i>
                      Mark Selected as Read ({selectedNotifications.length})
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => fetchNotifications(currentPage)}
                    disabled={loading}
                  >
                    <i className="fas fa-sync-alt me-1"></i>
                    Refresh
                  </Button>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body className="p-0">
              {error && (
                <Alert variant="danger" className="m-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Loading notifications...</p>
                </div>
              ) : notifications?.notifications?.length > 0 ? (
                <>
                  <div className="notifications-header p-3 border-bottom bg-light">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        id="selectAll"
                      />
                      <label className="form-check-label text-muted" htmlFor="selectAll">
                        Select All
                      </label>
                    </div>
                  </div>

                  <div className="notifications-list">
                    {notifications.notifications.map((notification, index) => (
                      <div
                        key={notification.id || index}
                        className={`notification-item ${notification.IsRead === 0 ? 'unread' : 'read'} ${
                          selectedNotifications.includes(notification.id) ? 'selected' : ''
                        }`}
                      >
                        <div className="notification-checkbox">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedNotifications.includes(notification.id)}
                            onChange={() => handleSelectNotification(notification.id)}
                          />
                        </div>
                        
                        <div className="notification-icon">
                          <i className={`${getNotificationIcon(notification.Content)} text-${getNotificationColor(notification.Content)}`}></i>
                        </div>

                        <div 
                          className="notification-content"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="notification-text">
                            <div dangerouslySetInnerHTML={{ __html: notification.Content }}></div>
                          </div>
                          <div className="notification-meta">
                            <small className="text-muted">
                              {notification.CreatedAt && formatDate(notification.CreatedAt)}
                            </small>
                            {notification.IsRead === 0 && (
                              <Badge bg="primary" className="ms-2">New</Badge>
                            )}
                          </div>
                        </div>

                        <div className="notification-actions">
                          {notification.IsRead === 0 && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification);
                              }}
                            >
                              <i className="fas fa-check"></i>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 border-top bg-light">
                    {renderPagination()}
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-bell-slash fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No notifications found</h5>
                  <p className="text-muted">You don't have any notifications right now!</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      <ViewTransferRequestModal
        show={viewTransfer}
        onHide={() => setViewTransfer(false)}
        record={record}
        requestType={requestType}
        notification={currentNotification}
      />

      <ViewMessageAnotherPharmacyModal
        show={viewMessageAnotherPharmacyModalShow}
        onHide={() => setViewMessageAnotherPharmacyModalShow(false)}
        record={record}
        notification={currentNotification}
      />
    </Container>
  );
};

export default Notifications;
